import styled from "@mui/system/styled";
import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Typography, Input, Box, FormControl, Select, Option, Avatar, Button,
  SvgIcon,
  Divider,
} from "@mui/joy";

import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";

import { supabase } from "../../supabaseClient";

import { Student } from "../../types";
import { GradeLevel, GradeLevelLabels } from "../../types/Grade";
import { fetchProfilePicture, getInitials, getProfilePictureName, deleteProfilePicture } from "../../utils";

import ImageCropModal from "../modals/ImageCropModal";

const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
  height: 1px;
  white-space: nowrap;
`;

const studentSchema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  preferred_first_name: yup.string().optional(),
  date_of_birth: yup.string().required("Date of birth is required"),
  grade_level: yup.mixed<GradeLevel>().required("Grade level is required"),
});

const ageToGradeLevel: { [age: number]: GradeLevel } = {
  4: GradeLevel.PreK,
  5: GradeLevel.Kindergarten,
  6: GradeLevel.First,
  7: GradeLevel.Second,
  8: GradeLevel.Third,
  9: GradeLevel.Fourth,
  10: GradeLevel.Fifth,
  11: GradeLevel.Sixth,
  12: GradeLevel.Seventh,
  13: GradeLevel.Eighth,
  14: GradeLevel.Ninth,
  15: GradeLevel.Tenth,
  16: GradeLevel.Eleventh,
  17: GradeLevel.Twelfth,
};

const getGradeLevelByAge = (age: number): GradeLevel => {
  return ageToGradeLevel[age] || GradeLevel.PreSchool; // Default to PreSchool if age is out of range
};

interface StudentFormValues {
  first_name: string;
  last_name: string;
  preferred_first_name?: string;
  date_of_birth: string;
  grade_level: GradeLevel;
}

interface AddEditStudentModalProps {
  student?: Student;
  onStudentUpdated?: () => void;
}

const AddEditStudentModal: React.FC<AddEditStudentModalProps> = ({ student, onStudentUpdated }) => {
  const { closeModal, setModalTitle, registerSubmitCallback } = useModal();
  const { showToast } = useToast();

  const [openCropModal, setOpenCropModal] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imagePreviewRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const defaultValues: StudentFormValues = useMemo(
    () => ({
      first_name: student?.first_name || "",
      last_name: student?.last_name || "",
      preferred_first_name: student?.preferred_first_name || "",
      date_of_birth: student?.date_of_birth || "",
      grade_level: (student?.grade_level as GradeLevel) || GradeLevel.PreSchool,
    }),
    [student]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<StudentFormValues>({
    defaultValues,
    resolver: yupResolver(studentSchema),
    mode: "onChange",
  });

  const handleCrop = (croppedImage: string) => {
    setImagePreview(croppedImage);
    imagePreviewRef.current = croppedImage;
    setOpenCropModal(false);
  };

  const onSubmit = useCallback(
    async (data: StudentFormValues) => {
      if (isSaving) return;
      setIsSaving(true);

      try {
        let updatedStudent;
        let newProfilePictureUUID = null;

        // Step 1: Handle profile picture upload first if there's a new image
        if (imagePreviewRef.current) {
          newProfilePictureUUID = uuidv4();
          const croppedBlob = await (await fetch(imagePreviewRef.current)).blob();
          const filePath = getProfilePictureName("students", newProfilePictureUUID);

          // Delete old profile picture if it exists
          if (student?.profile_picture_id) {
            await deleteProfilePicture("students", student.profile_picture_id);
          }

          // Upload new profile picture
          const { error: uploadError } = await supabase.storage
            .from("profile-pictures")
            .upload(filePath, croppedBlob, {
              cacheControl: "0",
              upsert: true,
            });

          if (uploadError) {
            throw new Error(`Failed to upload profile picture: ${uploadError.message}`);
          }
        }

        // Step 2: Save student data
        const studentData = {
          ...data,
          profile_picture_id: newProfilePictureUUID || student?.profile_picture_id || null,
        };

        if (student?.id) {
          // Update existing student
          const { data: updatedData, error } = await supabase
            .from("students")
            .update(studentData)
            .eq("id", student.id)
            .select();

          if (error) throw error;
          updatedStudent = updatedData?.[0];
        } else {
          // Add new student
          const { data: insertedData, error } = await supabase
            .from("students")
            .insert([studentData])
            .select();

          if (error) {
            throw error;
          }

          updatedStudent = insertedData?.[0];
        }

        if (!updatedStudent) {
          throw new Error("Failed to retrieve student data after save.");
        }

        showToast("Student saved successfully!", "success");
        if (onStudentUpdated) {
          onStudentUpdated();
        }
        closeModal();
      } catch (error) {
        console.error("Error saving student:", error);
        showToast(error instanceof Error ? error.message : "Error saving student.", "danger");
      } finally {
        setIsSaving(false);
      }
    },
    [student, imagePreviewRef, closeModal, showToast, isSaving, onStudentUpdated]
  );

  useEffect(() => {
    setModalTitle("Add/Edit Student");
    registerSubmitCallback(async () => {
      const result = handleSubmit(onSubmit);
      await result();
    });

    if (student?.id && !imagePreviewRef.current) {
      fetchProfilePicture("students", student.profile_picture_id).then((profilePicture) => {
        if (profilePicture !== imagePreviewRef.current) {
          setImagePreview(profilePicture);
        }
      });
    }
  }, [setModalTitle, handleSubmit, onSubmit, registerSubmitCallback, student?.id, student?.profile_picture_id]);

  useEffect(() => {
    imagePreviewRef.current = imagePreview;
  }, [imagePreview]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log("File selected, updating imagePreview...");
        setImagePreview(reader.result as string); // Update with the selected image
        setOpenCropModal(true); // Open the crop modal
        console.log("Image Preview After Set: ", imagePreview);
      };
      reader.readAsDataURL(file);
    } else {
      console.log("No file selected, clearing imagePreview...");
      setImagePreview(null); // Clear the preview if no file is selected
    }
  };

  const handleDateOfBirthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateOfBirth = new Date(event.target.value);
    const today = new Date();
    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDifference = today.getMonth() - dateOfBirth.getMonth();

    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < dateOfBirth.getDate())) {
      age--;
    }

    const probableGradeLevel = getGradeLevelByAge(age);
    setValue("grade_level", probableGradeLevel);
  };

  return (
    <Box sx={{ width: "600px", height: "400px", position: "relative" }}>
      <form>
        <Box display="flex" justifyContent="center" mb={2}>
          {imagePreview ? (
            <Avatar src={imagePreview} alt="Profile Picture" sx={{ width: 96, height: 96 }} />
          ) : (
            <Avatar sx={{ width: 96, height: 96 }}>{getInitials(defaultValues) || "HC"}</Avatar>
          )}
        </Box>
        <Box display="flex" justifyContent="center" mt={2} mb={3}>
          <Button
            component="label"
            variant="outlined"
            color="neutral"
            startDecorator={
              <SvgIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                  />
                </svg>
              </SvgIcon>
            }
          >
            Upload a Photo
            <VisuallyHiddenInput type="file" onChange={handleFileChange} />
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" gap={2} sx={{ width: "100%", flexWrap: "wrap" }}>
          <FormControl sx={{ flex: 1, minWidth: "200px" }}>
            <Typography component="label" htmlFor="first_name">First Name</Typography>
            <Controller
              control={control}
              name="first_name"
              render={({ field }) => (
                <Input {...field} id="first_name" placeholder="First name" error={!!errors.first_name} />
              )}
            />
            {errors.first_name && <Typography color="danger">{errors.first_name.message}</Typography>}
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: "200px" }}>
            <Typography component="label" htmlFor="last_name">Last Name</Typography>
            <Controller
              control={control}
              name="last_name"
              render={({ field }) => (
                <Input {...field} id="last_name" placeholder="Last name" error={!!errors.last_name} />
              )}
            />
            {errors.last_name && <Typography color="danger">{errors.last_name.message}</Typography>}
          </FormControl>
        </Box>

        <Box display="flex" gap={2} sx={{ width: "100%", flexWrap: "wrap", mt: 3 }}>
          <FormControl sx={{ flex: 1, minWidth: "200px" }}>
            <Typography component="label" htmlFor="preferred_first_name">Preferred First Name (Optional)</Typography>
            <Controller
              control={control}
              name="preferred_first_name"
              render={({ field }) => (
                <Input {...field} id="preferred_first_name" placeholder="e.g., Emme" error={!!errors.preferred_first_name} />
              )}
            />
            {errors.preferred_first_name && <Typography color="danger">{errors.preferred_first_name.message}</Typography>}
          </FormControl>
        </Box>

        <Box display="flex" gap={2} sx={{ width: "100%", flexWrap: "wrap", mt: 3 }}>
          <FormControl sx={{ flex: 1, minWidth: "200px" }}>
            <Typography component="label" htmlFor="date_of_birth">Date of Birth</Typography>
            <Controller
              control={control}
              name="date_of_birth"
              render={({ field }) => (
                <Input
                  {...field}
                  id="date_of_birth"
                  type="date"
                  onChange={(e) => {
                    field.onChange(e);
                    handleDateOfBirthChange(e); // Trigger grade level selection
                  }}
                  error={!!errors.date_of_birth}
                />
              )}
            />
            {errors.date_of_birth && <Typography color="danger">{errors.date_of_birth.message}</Typography>}
          </FormControl>
        </Box>

        <FormControl sx={{ mt: 3, minWidth: "200px" }}>
          <Typography component="label" htmlFor="grade_level">Grade Level</Typography>
          <Controller
            control={control}
            name="grade_level"
            render={({ field }) => (
              <Select
                {...field}
                id="grade_level"
                placeholder="Select grade level"
                value={field.value || ""} // Ensure the value is managed
                onChange={(event, newValue) => field.onChange(newValue)} // Update form state on change
              >
                {Object.entries(GradeLevelLabels).map(([value, label]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            )}
          />
          {errors.grade_level && <Typography color="danger">{errors.grade_level.message}</Typography>}
        </FormControl>

        {/* ImageCropModal integration */}
        <ImageCropModal
          open={openCropModal}
          initialImage={imagePreview} // Ensure new image is passed to the modal
          onClose={() => setOpenCropModal(false)}
          onCrop={handleCrop}
        />
      </form>
    </Box>
  );
};

export default AddEditStudentModal;
